import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import css from './CreativeAITags.css';
import { CreativeType } from '../../types';
import { GET_CREATIVE_TAGS } from '../../queries';
import { DELETE_CREATIVE_TAGS } from '../../mutations';
import { GalleryContext, TRACK_EVENT_PREFIX } from '../../consts';
import { Button, Table, TableCell, WarningMessage } from '../../../components/widgets';
import TableCellTags from '../../../components/widgets/TableCellTags';
import { DimensionCategoryToColor, NO_TAGS_COLOR } from '../../dimensionsUtils';
import SearchTextField from '../../../components/molecules/SearchTextField/SearchTextField';
import { IconSizes } from '../../../components/foundations/Icon';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import Snackbar from '../../../components/widgets/Snackbar/Snackbar';
import { trackMixpanelEvent } from '../../../utils/general';

export const SKELETONS = Array(4).fill({});
export const NO_AI_DIMENSIONS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_AI_DIMENSIONS';
export const NO_AI_TAGS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_AI_TAGS';
export const NO_FILTERED_AI_TAGS = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.NO_FILTERED_AI_TAGS';
export const SEARCH_TAGS_PLACEHOLDER = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.SEARCH_TAGS_PLACEHOLDER';
export const NO_TAGS_AVAILABLE = 'STATIC.PAGES.CREATIVE_GALLERY.NO_TAGS_AVAILABLE';
export const DIMENSION_NAME_CELL_TEST_ID = 'dimension-name-cell';
const SNACKBAR_AUTO_HIDE_DURATION = 3000;

const getMessageContent = (noTagsData, filteredCreativeAITags) =>
    noTagsData ? NO_AI_TAGS : !filteredCreativeAITags.length ? NO_FILTERED_AI_TAGS : '';

const getTagsTableMetadata = (isLoading, translate, editMode, onDeleteTag) => [
    {
        name: 'dimensionName',
        displayName: 'Dimensions',
        cellComponent: TableCell,
        cellProps: {
            className: css.dimensionNameTableCell,
            dataTestId: DIMENSION_NAME_CELL_TEST_ID,
            isLoading,
            loaderClass: css.cellLoader,
        },
    },
    {
        name: 'tags',
        displayName: 'Values',
        cellComponent: TableCellTags,
        cellValues: [
            {
                propName: 'color',
                dataKey: 'category',
                transformation: category => DimensionCategoryToColor[category],
            },
            {
                propName: 'dimensionName',
                dataKey: 'dimensionName',
            },
        ],
        cellProps: {
            isLoading,
            loaderClass: css.cellLoader,
            tagClassName: css.tag,
            defaultTag: translate(NO_TAGS_AVAILABLE),
            defaultColor: NO_TAGS_COLOR,
            isEditMode: editMode,
            onDeleteTag,
        },
    },
];

const CreativeAITagsTable = ({ app, source, imageHash, translate, onHasUnsavedChanges = () => {} }) => {
    const { creativesTags } = useContext(GalleryContext);
    const [search, setSearch] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [deleteCount, setDeleteCount] = useState(0);
    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '', title: '' });

    const deletedTagsRef = useRef(new Map());

    const creativeFetchedTags = useMemo(() => {
        return creativesTags?.filter(creativeTag => creativeTag.imageHash === imageHash);
    }, [creativesTags, imageHash]);

    const shouldSkipQuery = !!creativeFetchedTags?.length;

    const { loading: queryIsLoading, data: creativeTagsResponse } = useQuery(GET_CREATIVE_TAGS, {
        variables: {
            appId: app,
            imageHashes: [imageHash],
        },
        skip: shouldSkipQuery,
    });

    const isLoading = !shouldSkipQuery && queryIsLoading;
    const tags = creativeFetchedTags || creativeTagsResponse?.creativeTags?.tags || [];

    useEffect(() => {
        const hasUnsavedChanges = isEditing && deleteCount;
        onHasUnsavedChanges(hasUnsavedChanges);
    }, [isEditing, deleteCount]);

    useEffect(() => {
        if (!isLoading && !isEditing && deletedTagsRef.current.size && creativeTagsResponse?.creativeTags?.tags) {
            deletedTagsRef.current.clear();
        }

        return () => deletedTagsRef.current.clear();
    }, [creativeTagsResponse, isEditing, isLoading]);

    const showSnackBar = (type, title, message) => {
        setSnackbar({ open: true, type, title, message });
    };

    const hideSnackBar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const displayedCreativeAITags = useMemo(() => {
        return tags.map(aiDimension => {
            const deletedSet = deletedTagsRef.current.get(aiDimension.dimensionName);
            if (!deletedSet || !deletedSet.size) {
                return aiDimension;
            }

            const filtered = aiDimension.tags.filter(tag => !deletedSet.has(tag));
            return {
                ...aiDimension,
                tags: filtered,
            };
        });
    }, [tags, deleteCount]);

    const [deleteCreativeTagsMutation, { loading: isDeletingTags }] = useMutation(DELETE_CREATIVE_TAGS, {
        update(cache, { data }) {
            if (data.deleteCreativeTags.ok) {
                cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: 'creativeTags',
                });
                cache.gc();
            }
        },
        onCompleted: data => {
            if (data.deleteCreativeTags.ok) {
                setIsEditing(false);
                setDeleteCount(0);
                setShowSaveConfirmation(false);
                showSnackBar(
                    'success',
                    translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.SUCCESS.TITLE'),
                    translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.SUCCESS.MESSAGE')
                );
            } else {
                setShowSaveConfirmation(false);
                showSnackBar(
                    'error',
                    translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.TITLE'),
                    translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.MESSAGE')
                );
            }
        },
        onError: () => {
            setShowSaveConfirmation(false);
            showSnackBar(
                'error',
                translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.TITLE'),
                translate('STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SNACKBAR.ERROR.UNEXPECTED')
            );
        },
    });

    const noTagsData = !displayedCreativeAITags.some(aiDimension => !!aiDimension.tags.length);

    const filteredCreativeAITags = displayedCreativeAITags
        .map(aiDimension => {
            const searchLower = search.toLowerCase();
            const matchesDimension = aiDimension.dimensionName.toLowerCase().includes(searchLower);
            if (matchesDimension) return aiDimension;

            const filteredTags = aiDimension.tags.filter(tag => tag.toLowerCase().includes(searchLower));
            return filteredTags.length ? { ...aiDimension, tags: filteredTags } : null;
        })
        .filter(Boolean);

    const noTagsDisplayed = !isLoading && (!filteredCreativeAITags.length || noTagsData);

    const onDeleteTag = useCallback((dimensionName, tagToDelete) => {
        const tagsSet = deletedTagsRef.current.get(dimensionName) || new Set();
        tagsSet.add(tagToDelete);
        deletedTagsRef.current.set(dimensionName, tagsSet);

        const newCount = Array.from(deletedTagsRef.current.values()).reduce((sum, set) => sum + set.size, 0);
        setDeleteCount(newCount);
    }, []);

    const onSaveChanges = useCallback(() => {
        if (deleteCount === 0) {
            setIsEditing(false);
            return;
        }

        setShowSaveConfirmation(true);
    }, [deleteCount]);

    const handleConfirmSave = useCallback(() => {
        const tagsToDelete = Array.from(deletedTagsRef.current.entries()).map(([dimensionName, tagsSet]) => ({
            dimensionName,
            tagValues: Array.from(tagsSet),
        }));

        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Delete Tags', {
            app,
            source,
        });

        deleteCreativeTagsMutation({
            variables: {
                deleteCreativeTagsNode: {
                    appId: app,
                    imageHash,
                    tagsToDelete,
                },
            },
        });
    }, [app, source, imageHash, deleteCreativeTagsMutation]);

    const resetEditMode = () => {
        deletedTagsRef.current.clear();
        setIsEditing(false);
        setDeleteCount(0);
    };

    const onCancelEdit = useCallback(() => {
        if (deleteCount) {
            setShowDiscardConfirmation(true);
        } else {
            resetEditMode();
        }
    }, [deleteCount]);

    const handleConfirmDiscard = useCallback(() => {
        resetEditMode();
        setShowDiscardConfirmation(false);
    }, []);

    return (
        <div className={css.creativeTags}>
            <GeneralPopup
                open={showSaveConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_ACTION"
                rejectText="STATIC.BUTTONS.CANCEL"
                onAccept={handleConfirmSave}
                onReject={() => setShowSaveConfirmation(false)}
                showSpinner={isDeletingTags}
            />
            <GeneralPopup
                open={showDiscardConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmDiscard}
                onReject={() => setShowDiscardConfirmation(false)}
            />
            {snackbar.type && (
                <Snackbar
                    title={snackbar.title}
                    type={snackbar.type}
                    message={snackbar.message}
                    open={snackbar.open}
                    autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
                    onClose={hideSnackBar}
                />
            )}
            <div className={css.tagsTitle}>
                <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ALL_CREATIVE_TAGS" />
                {isEditing ? (
                    <div className={css.tagsActions}>
                        <Button type="flat" level="level2" onClick={onCancelEdit} disabled={isDeletingTags}>
                            <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.CANCEL_TAGS" />
                        </Button>
                        <Button
                            type="secondary"
                            level="level2"
                            onClick={onSaveChanges}
                            disabled={isDeletingTags || deleteCount === 0}
                            showSpinner={isDeletingTags}
                        >
                            {deleteCount ? (
                                <Translate
                                    id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_TAGS"
                                    data={{ count: deleteCount }}
                                />
                            ) : (
                                <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.SAVE_ACTION" />
                            )}
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="secondary"
                        level="level2"
                        Icon="edit"
                        onClick={() => setIsEditing(true)}
                        disabled={isDeletingTags}
                    >
                        <Translate id="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.EDIT_TAGS" />
                    </Button>
                )}
            </div>
            <SearchTextField
                placeholder={SEARCH_TAGS_PLACEHOLDER}
                clearable
                search={search}
                onSearchChange={setSearch}
                iconSize={IconSizes.X_SMALL}
                disabled={noTagsDisplayed || isLoading}
            />
            <div className={css.tagsTableContainer}>
                <Table
                    tableClass={css.tagsTable}
                    metadata={getTagsTableMetadata(isLoading, translate, isEditing, onDeleteTag)}
                    data={isLoading ? SKELETONS : noTagsDisplayed ? [] : filteredCreativeAITags}
                    theadClass={css.tagsTableHeader}
                    thClass={css.tagsTableHeaderCell}
                    trClass={css.tagsTableRowCell}
                    zebraTable={false}
                />
                {noTagsDisplayed && (
                    <div className={css.messageContainer}>
                        {!tags.length ? (
                            <WarningMessage
                                showIcon={false}
                                className={css.warningMessage}
                                message={NO_AI_DIMENSIONS}
                            />
                        ) : (
                            <Translate id={getMessageContent(noTagsData, filteredCreativeAITags)} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

CreativeAITagsTable.propTypes = {
    app: PropTypes.string.isRequired,
    source: PropTypes.string,
    imageHash: CreativeType.imageHash.isRequired,
    translate: PropTypes.func,
    onHasUnsavedChanges: PropTypes.func,
};

export default withLocalize(CreativeAITagsTable);
